import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { ListItem, ListItemText, Slider, Box } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { previewHistoryBatch } from '../../pages/CreatePage/store/extensions';
import { calculateRangeValue } from '../../../utilities/mathUtilities';
import SliderTitle from './SliderTitle';
import { SliderInput } from './SliderInput';

const styles = theme => ({
    input: {
        width: 38,
        fontSize: 14,
        fontWeight: 500
    },
    listItemText: {
        paddingRight: 0,
        marginBottom: 0,
    },
    hideArrows: {
        '&::-webkit-inner-spin-button': {
            WebkitAppearance: 'none' 
        },
        '&::-webkit-outer-spin-button': {
            WebkitAppearance: 'none'
        },
    },
    sliderWrapper: {
        display: 'flex', 
        alignItems: 'center', 
        height: '35px'
    },
    inputBoxMin: {
        marginRight: theme.spacing(2), 
        display: 'flex'
    },
    inputBoxMax: {
        display: 'flex'
    },
    slider: {
        flex: 1,
        marginRight: theme.spacing(2),
        marginTop: theme.spacing(3),
        "& .MuiSlider-valueLabel": {
            backgroundColor: theme.palette.primary.main,
        }
    }
});

const SliderRangeListItem = memo(({
    title,
    hideArrows,
    displayMultiplier = 1,
    onValueChange,
    toolTip,
    value = [0, 100],
    step = 1,
    min = 0,
    max = 100,
    scale,
    classes,
    preventRound = false
}) => {
    // Memoize initial value calculation
    const initialValue = useMemo(() =>
        calculateRangeValue(value, displayMultiplier, preventRound, step, min, max),
        [value, displayMultiplier, preventRound, step, min, max]
    );

    const [displayValue, setDisplayValue] = useState(initialValue);
    const isFirstRender = useRef(true);

    // Memoize handlers
    const handleSliderChange = useCallback((_, value) => {
        setDisplayValue(value);
    }, []);

    const handlerInputChange = useCallback((index) => (event) => {
        let newValue = Number(event.target.value);
        newValue = Math.min(Math.max(newValue, min), max);
        
        setDisplayValue(prev => {
            const newArray = [...prev];
            newArray[index] = newValue;
            
            if (newArray[0] > newArray[1]) {
                return [newArray[1], newArray[0]];
            }
    
            return newArray;
        });
    }, [min, max]);

    const handleMinInputChange = useMemo(() =>
        handlerInputChange(0),
        [handlerInputChange]
    );

    const handleMaxInputChange = useMemo(() =>
        handlerInputChange(1),
        [handlerInputChange]
    );

    const onFocus = useCallback(() => {
        previewHistoryBatch.start();
    }, []);

    const onBlur = useCallback(() => {
        previewHistoryBatch.end();
    }, []);

    const onSliderChangeCommitted = useCallback(() => {
        previewHistoryBatch.end();
    }, []);

    // Update parent when value changes
    useEffect(() => {
        // Skip the first render to avoid calling onValueChange during initialization
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        
        const [minValue, maxValue] = displayValue;
        onValueChange([
            minValue / displayMultiplier,
            maxValue / displayMultiplier
        ]);
    }, [displayValue, displayMultiplier, onValueChange]);

    // Update local state when props change
    useEffect(() => {
        const newValue = calculateRangeValue(value, displayMultiplier, preventRound, step, min, max);
        if (newValue[0] !== displayValue[0] || newValue[1] !== displayValue[1]) {
            setDisplayValue(newValue);
        }
    }, [value, displayMultiplier, preventRound, step, min, max]);

    const inputProps = useMemo(() => ({
        step,
        min,
        max,
        hideArrows,
        classes,
        onFocus,
        onBlur
    }), [step, min, max, hideArrows, classes, onFocus, onBlur]);

    return (
        <ListItem className={classes.paddingBottom}>
            <ListItemText className={classes.listItemText}>
                <SliderTitle title={title} toolTip={toolTip} />
                <Box className={classes.sliderWrapper}>
                    <Box className={classes.inputBoxMin}>
                        <SliderInput
                            {...inputProps}
                            value={displayValue[0]}
                            onChange={handleMinInputChange}
                        />
                    </Box>
                    <Slider
                        size='small'
                        value={displayValue}
                        onChange={handleSliderChange}
                        step={step}
                        min={min}
                        max={max}
                        valueLabelDisplay='auto'
                        scale={scale}
                        onChangeCommitted={onSliderChangeCommitted}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        className={classes.slider}
                    />
                    <Box className={classes.inputBoxMax}>
                        <SliderInput
                            {...inputProps}
                            value={displayValue[1]}
                            onChange={handleMaxInputChange}
                        />
                    </Box>
                </Box>
            </ListItemText>
        </ListItem>
    );
});

export default withStyles(SliderRangeListItem, styles);